// Archive rows as a person reads them: one entry per clear, not per value row.
//
// Run: deno test --allow-read src/features/pnlAnalytics/archiveBatches.test.ts
// Run: npm run verify:pnl-archive-batches
//
// Pure, no React and no supabase, so the two rules below can be tested rather
// than reviewed:
//
//   1. A batch counts as RESTORED only when every row in it is restored. A
//      partial restore reading as done would hide exactly the case the restore
//      RPC reports honestly, where a month was retyped since the clear and was
//      deliberately left alone.
//   2. A row with no batch id left by a route that was not the clear dialog,
//      which the archive trigger records with the reason "direct_delete". Those
//      are kept and grouped, never folded into a real batch and never dropped:
//      "money left the value table and nobody pressed the button that does
//      that" is the single most useful thing this screen can show. They carry
//      no batch id, so the screen offers no restore button for them rather than
//      one that would fail.

import { formatMonthLabel } from "./pnlAnalyticsFormat";

export interface ArchiveRow {
  clear_batch_id: string | null;
  project_id: string;
  category: string;
  month_date: string;
  amount: number | string;
  source: string;
  cleared_at: string;
  cleared_by: string | null;
  reason: string | null;
  restored_at: string | null;
}

/** One clear, as a person reads it: what left, from where, when, and by whom. */
export interface ArchiveBatch {
  batchId: string | null;
  projectId: string;
  category: string;
  rowCount: number;
  total: number;
  firstMonth: string;
  lastMonth: string;
  clearedAt: string;
  clearedBy: string | null;
  reason: string | null;
  restored: boolean;
}

/**
 * Groups archive rows into batches.
 *
 * A NULL batch id means the rows were deleted by a route that was not mode B,
 * which the trigger records with the reason "direct_delete". Those are grouped
 * per project, category and day rather than being folded together or dropped,
 * because "money left this table and nobody pressed the button that does that"
 * is exactly the thing an admin should be able to see. They carry no batch id,
 * so they cannot be restored as a unit, and the screen says so instead of
 * offering a button that would fail.
 */
export function groupArchiveBatches(rows: readonly ArchiveRow[]): ArchiveBatch[] {
  const batches = new Map<string, ArchiveBatch & { months: string[] }>();
  for (const row of rows) {
    const key = row.clear_batch_id ?? `direct:${row.project_id}:${row.category}:${row.cleared_at.slice(0, 10)}`;
    let batch = batches.get(key);
    if (!batch) {
      batch = {
        batchId: row.clear_batch_id,
        projectId: row.project_id,
        category: row.category,
        rowCount: 0,
        total: 0,
        firstMonth: row.month_date,
        lastMonth: row.month_date,
        clearedAt: row.cleared_at,
        clearedBy: row.cleared_by,
        reason: row.reason,
        // A batch counts as restored only when EVERY row in it is, so a
        // partial restore reads as outstanding rather than as done.
        restored: true,
        months: [],
      };
      batches.set(key, batch);
    }
    batch.rowCount += 1;
    batch.total += Number(row.amount) || 0;
    batch.months.push(row.month_date);
    if (row.month_date < batch.firstMonth) batch.firstMonth = row.month_date;
    if (row.month_date > batch.lastMonth) batch.lastMonth = row.month_date;
    if (row.cleared_at < batch.clearedAt) batch.clearedAt = row.cleared_at;
    if (!row.restored_at) batch.restored = false;
  }
  return [...batches.values()]
    .map(({ months: _months, ...batch }) => batch)
    .sort((a, b) => b.clearedAt.localeCompare(a.clearedAt));
}

/** "Aug 2026", or "Feb 2026 to Aug 2026" when the batch spans months. */
export function describeMonthSpan(batch: ArchiveBatch): string {
  if (batch.firstMonth === batch.lastMonth) return formatMonthLabel(batch.firstMonth);
  return `${formatMonthLabel(batch.firstMonth)} to ${formatMonthLabel(batch.lastMonth)}`;
}

